// Consumer robustness fixtures derived from an older Workbench mirror DTO.
// These deliberately partial/absent/malformed inputs are NEGATIVE/compatibility
// fixtures, NOT complete current durable repository output or a live population.
// Positive repository queue/count integration uses generated writerScenarios.json.
// Missing fields here test unread UI behavior; they do not prove the current writer
// omits those fields. Synthetic schedule examples do not prove scheduling works.

import type { ContentReviewItem } from "../../../services/contentReviewService";

/**
 * The floor every fixture builds on: only the keys `toReviewDto` ALWAYS emits.
 * Anything conditional is added per-flavour, by spreading — the same mechanism
 * the translator uses, so "absent" here means what it means on the wire.
 */
/** Hours ago, as an ISO string. Relative so the pack never expires. */
const hoursAgo = (h: number): string =>
  new Date(Date.now() - h * 3_600_000).toISOString();

const base = (id: string): ContentReviewItem => ({
  id,
  reel_id: `reel-${id}`,
  subject: "Untitled",
  template: "unknown", // the translator's own fallback: "this builder did not say"
  template_version: "unknown",
  status: "awaiting_review",
  verdict: "pending",
  // Widened at contentMirror.ts:252 — true for nearly every live card, so it is
  // NOT an attention signal. Fixtures carry the widening so nobody builds on it.
  needs_attention: true,
  errors: [],
  revision_capable: true,
  capability_reason: "",
  video_available: true,
  frame_sheet_available: true,
  revision_number: 0,
  changed_fields: [],
  q_codes: [],
  // The mock shows an age on every review row, coloured when it has been
  // waiting too long. `updated_at` is the row's LAST CHANGE, not creation.
  updated_at: hoursAgo(2),
});

/** The card that never breaks anything. */
export const clean: ContentReviewItem = {
  ...base("clean-1"),
  subject: "The Juniper — rooftop and the light",
  template: "property_tour",
  scenes: [
    {
      id: "s1",
      label: "Living room, wide",
      beat_role: "open",
      caption: "light for days",
      clip_id: "c1",
      cue_id: null,
      duration_sec: 5,
      offset_sec: 0,
    },
    {
      id: "s2",
      label: "Rooftop, skyline",
      beat_role: "close",
      caption: "then the roof",
      clip_id: "c2",
      cue_id: null,
      duration_sec: 6,
      offset_sec: 5,
    },
  ],
  post_kit: {
    caption: "The Juniper, Midtown. Rooftop that earns the elevator ride.",
    first_comment: "",
    hashtags: ["atlanta", "midtown"],
  },
};

/**
 * 🔴 property_name ABSENT and the card is otherwise FINE.
 *
 * Rendered before the builder populated the property fields. `errors` is empty
 * on purpose: this is NOT a broken package and must not be flagged as one. It
 * is the single biggest predictor of the real distribution — the split is
 * package AGE, not content.
 */
export const preBuilder: ContentReviewItem = {
  ...base("prebuilder-1"),
  updated_at: hoursAgo(26),
  subject: "Midtown property tour",
  template: "property_tour",
  // property_name and property_id: ABSENT. Not null. Not "".
};

/**
 * FOUR states from ONE cause — no scenes resolved, so several fields go quiet
 * together. Co-occurrence, not four independent empty states to design for.
 */
export const noRevisionState: ContentReviewItem = {
  ...base("norev-1"),
  // Past the stale threshold, so the coloured-age state is visible in the pack.
  updated_at: hoursAgo(150),
  subject: "The Hadley — tour",
  template: "property_tour",
  // scenes: ABSENT (the translator omits it rather than sending [])
  // parent_reel_id: ABSENT
  revision_number: 0,
};

/** A second version of an existing reel. Groups into ONE tile with its parent. */
export const revision: ContentReviewItem = {
  ...base("rev-2"),
  reel_id: "reel-hadley-v2",
  subject: "The Hadley — tour",
  template: "property_tour",
  status: "revised",
  parent_reel_id: "reel-hadley-v1",
  revision_number: 2,
  changed_fields: ["caption", "clip"],
};

/** The parent of `revision`. Must wear "Replaced by a newer version". */
export const revisionParent: ContentReviewItem = {
  ...base("rev-1"),
  // Its OWN reel_id. It previously reused noRevisionState's, which silently
  // chained three unrelated cards into one tile reading "3 versions" — a
  // fixture bug that looked exactly like a grouping bug.
  reel_id: "reel-hadley-v1",
  subject: "The Hadley — tour",
  template: "property_tour",
  revision_number: 1,
};

/**
 * 🔴 An approval that did not stick.
 *
 * Measured on prod: an applied P whose mirror row was rewritten back to
 * awaiting_review two hours later. Renders as an ordinary card today.
 */
export const approvedButReverted: ContentReviewItem = {
  ...base("approved-lost"),
  subject: "Emmi Midtown — tour",
  template: "property_tour",
  status: "awaiting_review",
  command: {
    request_id: "req-1",
    verdict: "P",
    state: "applied",
    applied: true,
  },
};

/** 🔴 An applied approval now filed as `parked` — the UI draws that "Rejected". */
export const approvedButParked: ContentReviewItem = {
  ...base("approved-parked"),
  subject: "The Juniper — tour",
  template: "property_tour",
  status: "parked",
  command: {
    request_id: "req-2",
    verdict: "P",
    state: "applied",
    applied: true,
  },
};

/** Approved and genuinely schedulable: an applied P WITH a caption. */
export const approvedWithKit: ContentReviewItem = {
  ...clean,
  id: "approved-kit",
  reel_id: "reel-approved-kit",
  subject: "Novel Midtown — tour",
  // Genuinely approved: the ROW says so. `approved` IS a legal status (053:86-88);
  // an earlier version of these fixtures encoded the opposite and was wrong.
  status: "approved",
  verdict: "P",
  command: {
    request_id: "req-3",
    verdict: "P",
    state: "applied",
    applied: true,
  },
};

/**
 * Approved with NO post kit — the Schedule control must refuse this one.
 * 8 awaiting_review + all 6 revised prod cards are in this state.
 */
export const approvedNoKit: ContentReviewItem = {
  ...base("approved-nokit"),
  subject: "Atlantic House — tour",
  template: "property_tour",
  status: "approved",
  verdict: "P",
  command: {
    request_id: "req-4",
    verdict: "P",
    state: "applied",
    applied: true,
  },
  // post_kit ABSENT — and it is NOT recoverable whether a caption was authored
  // and blank, or never written. postKitOf collapses both to undefined.
};

/** A lawless card: no dials, and the reason cannot be shown (it is flattened). */
export const lawless: ContentReviewItem = {
  ...base("lawless-1"),
  subject: "Grace Residences — the light, then the roof",
  template: "lawless",
  revision_capable: false,
  capability_reason: "Revision unavailable for this package",
};

/** A card whose video never rendered. `video_available` false, not an error. */
export const noVideo: ContentReviewItem = {
  ...base("novideo-1"),
  subject: "The Hadley — tour",
  template: "property_tour",
  video_available: false,
  frame_sheet_available: false,
};

/** A genuinely broken package — the only flavour here that SHOULD be flagged. */
export const corrupt: ContentReviewItem = {
  ...base("corrupt-1"),
  subject: "Untitled",
  status: "parked",
  errors: ["PKG-SEAL: manifest does not match contents"],
  video_available: false,
  frame_sheet_available: false,
};

/**
 * `service_explainer` — 13 of 63 prod cards.
 *
 * It reaches `template` because `format_slug` FALLS THROUGH to it
 * (assembler/contentLineCore.ts:540 — `options.template ?? plan.format_slug ??
 * "unclassified"`). So the set of possible templates is OPEN-ENDED: any new
 * format_slug becomes a template with no code change and no migration. A
 * hardcoded chip list drops this card and the 12 like it.
 */
export const formatSlugTemplate: ContentReviewItem = {
  ...base("legacy-1"),
  subject: "How the free locator works",
  template: "service_explainer",
};

/**
 * 🔴 THE SECOND FALLBACK, and it is not the same as the first.
 *
 *   "unclassified"  contentLineCore.ts:540 — the PLAN named no template and no
 *                   format_slug.
 *   "unknown"       reviewCockpit.ts:822  — the META carried no template.
 *
 * Two different silences. A filter that special-cases one mislabels the other,
 * so the UI groups both as "type not recorded" and keeps the raw value.
 */
export const unclassifiedTemplate: ContentReviewItem = {
  ...base("unclassified-1"),
  subject: "Untitled reel",
  template: "unclassified",
};

/**
 * Real in prod (5 cards) and UNSOURCED: `git log -S` finds `url-phone-social`
 * nowhere in origin/main's history. The box runs a PINNED RELEASE, so a local
 * clone cannot answer what wrote it. The filter must show it anyway — refusing
 * to display a value because we cannot explain it is how 5 cards disappear.
 */
export const unsourcedTemplate: ContentReviewItem = {
  ...base("unsourced-1"),
  subject: "Call us — Midtown",
  template: "url-phone-social",
};

/** Rejected, plainly. */
export const rejected: ContentReviewItem = {
  ...base("rejected-1"),
  subject: "What $3,000 gets you in Midtown",
  template: "url-phone-social",
  status: "parked",
  verdict: "H",
};

/**
 * The queue to click-test against.
 *
 * Absent states DOMINATE, per the contract's §4: of these, only three are
 * fully-populated cards. That ratio is the point.
 */
export const REALISTIC_QUEUE: ContentReviewItem[] = [
  clean,
  preBuilder,
  noRevisionState,
  revisionParent,
  revision,
  approvedButReverted,
  approvedButParked,
  approvedWithKit,
  approvedNoKit,
  lawless,
  noVideo,
  corrupt,
  formatSlugTemplate,
  unclassifiedTemplate,
  unsourcedTemplate,
  rejected,
];
